import React, { ReactNode, useState } from 'react';
import {
  FiHome,
  FiSend,
  FiSettings,
  FiLogOut,
  FiChevronLeft,
  FiChevronRight,
  FiChevronDown,
  FiChevronUp,
} from 'react-icons/fi';
import { GrTransaction } from 'react-icons/gr';
import { VscAccount } from 'react-icons/vsc';
import { NavLink, useNavigate } from 'react-router-dom';
import Logo from '../assets/svgs/rynopay_logo.svg';
import MiniLogo from '../assets/svgs/ryno-new-logo.svg';
import ConfirmationModal from '../components/base/confirmation-dialog';
import { useAuthStore } from '../store';
import HeaderComponent from './component/header';

interface LayoutProps {
  children: ReactNode;
}

interface MenuItem {
  label: string;
  icon: React.ReactNode;
  path?: string;
  disabled?: boolean;
  subMenu?: MenuItem[];
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const verificationStatus = 0;
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isOpenLogoutDialog, setIsOpenLogoutDialog] = useState(false);
  const [openSubMenus, setOpenSubMenus] = useState<Record<string, boolean>>({});

  const { removeAuthStateFromStorage } = useAuthStore();

  const handleLogoutClick = () => setIsOpenLogoutDialog(true);

  const confirmLogout = () => {
    removeAuthStateFromStorage();
    setIsOpenLogoutDialog(false);
    navigate('/login');
  };

  const toggleSubMenu = (label: string) => {
    setOpenSubMenus((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  const menuItems: MenuItem[] = [
    {
      label: 'Dashboard',
      icon: <FiHome className="text-xl" />,
      path: '/dashboard',
    },
    {
      label: 'Send Money',
      icon: <FiSend className="text-xl" />,
      path: '/send',
    },
    {
      label: 'Transactions',
      icon: <GrTransaction className="text-xl" />,
      path: '/transactions',
    },
    {
      label: 'Account',
      icon: <VscAccount className="text-xl" />,
      path: '/account',
      disabled: verificationStatus !== 0,
    },
    {
      label: 'Settings',
      icon: <FiSettings className="text-xl" />,
      disabled: verificationStatus !== 0,
      subMenu: [
        {
          label: 'KYB Update',
          path: '/settings/kyb-update',
          icon: <FiSettings className="text-sm" />,
        },
      ],
    },
  ];

  return (
    <div className="flex h-screen bg-[#cde1f9cc]">
      <div
        className={`${
          isSidebarCollapsed ? 'w-20' : 'w-64'
        } bg-white border-r border-gray-200 flex flex-col transition-all duration-300`}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center">
            <img
              src={Logo}
              alt="Logo"
              className={`${isSidebarCollapsed ? 'hidden' : 'block'} h-8`}
            />
            <img
              src={MiniLogo}
              alt="Logo"
              className={`${!isSidebarCollapsed ? 'hidden' : 'block'} h-8`}
            />
          </div>
          <button
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            className="p-1"
          >
            {isSidebarCollapsed ? <FiChevronRight /> : <FiChevronLeft />}
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <nav className="mt-4 space-y-1">
            {menuItems.map((item) => {
              const isSubMenuOpen = openSubMenus[item.label] || false;
              const isDisabled = item.disabled;

              if (item.subMenu && item.subMenu.length > 0) {
                return (
                  <div key={item.label}>
                    <button
                      onClick={() => !isDisabled && toggleSubMenu(item.label)}
                      className={`w-full text-left flex items-center p-3 ${
                        window.location.pathname.includes(item.subMenu[0].path || '')
                          ? 'bg-[#bfd4ff] text-[#0040C2]'
                          : 'text-gray-700 hover:bg-gray-200'
                      } ${isDisabled ? 'pointer-events-none opacity-50' : ''}`}
                    >
                      {item.icon}
                      <span
                        className={`${
                          isSidebarCollapsed ? 'hidden' : 'block'
                        } ml-4 flex-1`}
                      >
                        {item.label}
                      </span>
                      {!isSidebarCollapsed && (
                        <span className="ml-auto">
                          {isSubMenuOpen ? <FiChevronUp /> : <FiChevronDown />}
                        </span>
                      )}
                    </button>
                    {isSubMenuOpen && (
                      <div className="ml-8 mt-1 space-y-1">
                        {item.subMenu.map((sub) => (
                          <NavLink
                            key={sub.label}
                            to={sub.path || '#'}
                            className={({ isActive }) =>
                              `block px-2 py-1 text-sm rounded ${
                                isActive
                                  ? 'bg-[#0040C2] text-white'
                                  : 'text-gray-700 hover:bg-gray-100'
                              }`
                            }
                          >
                            {sub.label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <NavLink
                  key={item.label}
                  to={item.path || '#'}
                  className={({ isActive }) =>
                    `flex items-center p-3 ${
                      isActive
                        ? 'bg-[#bfd4ff] text-[#0040C2]'
                        : 'text-gray-700 hover:bg-gray-200'
                    } ${isDisabled ? 'pointer-events-none opacity-50' : ''}`
                  }
                >
                  {item.icon}
                  <span
                    className={`${
                      isSidebarCollapsed ? 'hidden' : 'block'
                    } ml-4`}
                  >
                    {item.label}
                  </span>
                </NavLink>
              );
            })}
          </nav>
        </div>
        <div className="p-4 border-t border-gray-200">
          <a
            onClick={handleLogoutClick}
            className="flex items-center p-3 text-gray-700 hover:bg-gray-200 cursor-pointer"
          >
            <FiLogOut className="text-xl" />
            <span className={`${isSidebarCollapsed ? 'hidden' : 'block'} ml-4`}>
              Logout
            </span>
          </a>
        </div>
      </div>
      <div className="flex-1 flex flex-col">
        <HeaderComponent />
        <main className="flex-1 pt-8 px-12 bg-white overflow-y-auto">
          {children}
        </main>
      </div>
      <ConfirmationModal
        message={<>Are you sure you want to log out?</>}
        isOpen={isOpenLogoutDialog}
        onConfirm={confirmLogout}
        onCancel={() => setIsOpenLogoutDialog(false)}
        isLogout={true}
      />
    </div>
  );
};
export default Layout;
